// The new admin home — presentation rules (2026-10-21, docs/home-mockup-2026-10-21.html).
// Pure: no React, no DOM. The data rules live in worker lib/homeDashboard.ts.
import type { TodoItem, KpiNumbers } from './dashboardApi';

/** Rows shown before the fold line « + N autres … Tout voir → » (v2 cockpit). */
export const TODO_MAX_ROWS = 6;
/** Below this many parcels a rate is noise: « 4 colis — trop tôt ». */
export const MIN_SAMPLE = 10;

/** « 42 min », « 1 h 12 », « 5 h », « 13 j ». Pure. */
export function formatWait(sinceIso: string, nowMs: number): string {
  const mins = Math.max(0, Math.floor((nowMs - Date.parse(sinceIso)) / 60_000));
  if (mins < 60) return `${Math.max(1, mins)} min`;
  const h = Math.floor(mins / 60);
  if (h < 48) {
    const m = mins % 60;
    return h < 10 && m > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
  }
  return `${Math.floor(h / 24)} j`;
}

/** Red > 1 h, orange > 30 min, grey otherwise (same rule as the worker). Pure. */
export function waitTone(sinceIso: string, nowMs: number): 'red' | 'orange' | 'grey' {
  const mins = (nowMs - Date.parse(sinceIso)) / 60_000;
  if (mins > 60) return 'red';
  if (mins > 30) return 'orange';
  return 'grey';
}

export type TodoMode = 'mine' | 'all';

/**
 * The rows for a mode (« À moi + libres » / « Tout voir »), longest wait first,
 * and how many were folded for waiting more than 14 days. Pure.
 */
export function viewTodo(items: TodoItem[], mode: TodoMode): { rows: TodoItem[]; oldCount: number } {
  const inMode = items.filter((i) => mode === 'all' || i.mine);
  const rows = inMode.filter((i) => !i.old).sort((a, b) => Date.parse(a.since) - Date.parse(b.since));
  return { rows, oldCount: inMode.filter((i) => i.old).length };
}

/** A rate, or « too early » when fewer than MIN_SAMPLE make it. Pure. */
export function rateOrTooEarly(part: number, total: number): { kind: 'rate'; pct: number } | { kind: 'tooEarly'; n: number } {
  if (total < MIN_SAMPLE) return { kind: 'tooEarly', n: total };
  return { kind: 'rate', pct: Math.round((part / total) * 100) };
}

/** The orders list filtered to a pipeline step. Pure. */
export function pipelineHref(statuses: readonly string[]): string {
  return `/admin/orders?status=${statuses.join(',')}`;
}

/** « 1 917 » — grouped from 1 000 up with plain spaces (fr-FR's narrow space
 *  renders almost invisibly in Inter, and it skips 4-digit numbers). Pure. */
export function formatNum(n: number): string {
  const r = Math.round(n);
  const s = String(Math.abs(r)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return r < 0 ? `-${s}` : s;
}

/** « 6 002 DH ». Pure. */
export function formatDH(n: number): string {
  return `${formatNum(n)} DH`;
}

/** Change vs the previous period as a chip: ▲/▼ N %. `invert` = up is bad (costs). Pure. */
export function deltaChip(
  current: number | null | undefined,
  previous: number | null | undefined,
  opts: { invert?: boolean } = {},
): { text: string; tone: 'up' | 'down' | 'neutral' } | null {
  if (current == null || previous == null) return null;
  if (previous === 0) return current === 0 ? { text: '= 0 %', tone: 'neutral' } : null;
  const raw = ((current - previous) / Math.abs(previous)) * 100;
  const pct = Math.sign(raw) * Math.round(Math.abs(raw)); // −47.5 → −48, like the worker
  if (pct === 0) return { text: '= 0 %', tone: 'neutral' };
  const good = opts.invert ? pct < 0 : pct > 0;
  return { text: `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)} %`, tone: good ? 'up' : 'down' };
}

// ─── v2 cockpit (owner, 2026-10-22) ──────────────────────────────────────────

/** The home's period buttons (DateRangePicker presets; the picker stays elsewhere). */
export const HOME_PERIODS = [
  { key: 'today', label: "Aujourd'hui" },
  { key: '7d', label: '7 jours' },
  { key: '30d', label: '30 jours' },
  { key: 'thisMonth', label: 'Mois' },
] as const;
/** A quick button, or « custom » = dates picked in the calendar (owner 2026-10-22: « dates flexible like before »). */
export type HomePeriod = (typeof HOME_PERIODS)[number]['key'] | 'custom';

/** « 40 la sem. d'avant » — how the previous equal period is named. Pure. */
export function previousLabel(p: HomePeriod): string {
  if (p === 'today') return 'la veille';
  if (p === '7d') return "la sem. d'avant";
  if (p === '30d') return "les 30 j d'avant";
  return "la période d'avant";
}

/** « Livré · 7 j » — the delivered step's period tag. Pure. */
export function periodTag(p: HomePeriod): string {
  if (p === 'today') return 'auj.';
  if (p === '7d') return '7 j';
  if (p === '30d') return '30 j';
  if (p === 'custom') return 'période';
  return 'mois';
}

/** A KPI whose change vs the previous period is this bad gets the alert border. */
export const KPI_ALERT_PCT = 0.2;
/** Below this previous count a % change in orders is noise (same as the worker verdict). */
export const MIN_ORDERS_BASE = 5;
/** Delivery success: ≥ 90 % green, < 80 % alert (on ≥ MIN_SAMPLE parcels). */
export const DELIVERY_OK = 0.9;
export const DELIVERY_ALERT = 0.8;

export type KpiKey = 'revenue' | 'profit' | 'orders' | 'basket' | 'adPerOrder' | 'delivery';

const change = (cur: number | null | undefined, prev: number | null | undefined): number | null =>
  cur == null || prev == null || prev === 0 ? null : (cur - prev) / Math.abs(prev);

/** Is this KPI's change bad enough to flag the card? Pure. */
export function kpiAlert(key: KpiKey, cur: KpiNumbers, prev: KpiNumbers | null | undefined): boolean {
  switch (key) {
    case 'revenue': return (change(cur.revenue, prev?.revenue) ?? 0) < -KPI_ALERT_PCT;
    case 'profit': return (cur.profitNet != null && cur.profitNet < 0) || (change(cur.profitNet, prev?.profitNet) ?? 0) < -KPI_ALERT_PCT;
    case 'orders': return !!prev && prev.newOrders >= MIN_ORDERS_BASE && (change(cur.newOrders, prev.newOrders) ?? 0) < -KPI_ALERT_PCT;
    case 'basket': return (change(cur.avgBasket, prev?.avgBasket) ?? 0) < -KPI_ALERT_PCT;
    case 'adPerOrder': return (change(cur.adPerOrder, prev?.adPerOrder) ?? 0) > KPI_ALERT_PCT;
    case 'delivery': {
      const d = cur.delivery;
      return d.successRate != null && d.delivered + d.returned >= MIN_SAMPLE && d.successRate < DELIVERY_ALERT;
    }
  }
}

/** The delivery chip: the success rate, or « N colis — trop tôt ». Pure. */
export function deliveryChip(d: KpiNumbers['delivery']): { text: string; tone: 'up' | 'down' | 'neutral'; tooEarly: boolean; n: number } | null {
  const n = d.delivered + d.returned;
  if (n === 0) return null;
  const r = rateOrTooEarly(d.delivered, n);
  if (r.kind === 'tooEarly') return { text: `${n} colis — trop tôt`, tone: 'neutral', tooEarly: true, n };
  const tone = r.pct >= DELIVERY_OK * 100 ? 'up' : r.pct < DELIVERY_ALERT * 100 ? 'down' : 'neutral';
  return { text: `${r.pct} %`, tone, tooEarly: false, n };
}

/** « 8,1 » in French, « 8.1 » in English. Pure. */
export function formatDecimal(n: number, lang: string = 'fr'): string {
  const s = (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '');
  return lang === 'en' ? s : s.replace('.', ',');
}

/**
 * A sparkline as polyline point lists in a 100×h box: one segment per run of
 * known values (a null day is a gap, never a 0). A single known point draws a
 * flat dash. `connect`: join across null days instead — for averages, where a
 * day with no delivery has no value but nothing is missing. Pure.
 */
export function sparkSegments(values: Array<number | null | undefined>, h = 26, pad = 3, connect = false): string[] {
  const known = values.filter((v): v is number => v != null && Number.isFinite(v));
  if (!known.length) return [];
  const min = Math.min(...known);
  const max = Math.max(...known);
  const span = max - min;
  const n = values.length;
  const x = (i: number) => (n === 1 ? 50 : Math.round((i / (n - 1)) * 1000) / 10);
  const y = (v: number) => (span === 0 ? h / 2 : Math.round((pad + (1 - (v - min) / span) * (h - 2 * pad)) * 10) / 10);
  const segs: string[] = [];
  let cur: string[] = [];
  values.forEach((v, i) => {
    if (v == null || !Number.isFinite(v)) { if (!connect && cur.length) { segs.push(cur.join(' ')); cur = []; } return; }
    cur.push(`${x(i)},${y(v)}`);
  });
  if (cur.length) segs.push(cur.join(' '));
  return segs.map((s) => {
    if (s.includes(' ')) return s;
    const [px, py] = s.split(',').map(Number);
    return `${Math.max(0, px - 1)},${py} ${Math.min(100, px + 1)},${py}`;
  });
}

/** « mis à jour à l'instant » / « il y a 3 min » / « il y a 2 h ». Pure. */
export function updatedAgo(atMs: number, nowMs: number): { kind: 'now' } | { kind: 'min'; n: number } | { kind: 'h'; n: number } {
  const mins = Math.floor(Math.max(0, nowMs - atMs) / 60_000);
  if (mins < 1) return { kind: 'now' };
  if (mins < 60) return { kind: 'min', n: mins };
  return { kind: 'h', n: Math.floor(mins / 60) };
}

/** Counts per verb over the rows (for the filter pills). Pure. */
export function verbCounts(rows: TodoItem[]): Record<TodoItem['kind'], number> {
  const c = { validate: 0, reply: 0, preview: 0, ship: 0 };
  for (const r of rows) c[r.kind] += 1;
  return c;
}

/** The bars + line of « Ventes vs pub »: y positions in a w×h box. Pure. */
export function salesChartGeometry(
  points: Array<{ revenue: number; adSpend: number | null }>,
  w = 280, h = 110,
): { bars: Array<{ x: number; y: number; width: number; height: number }>; line: string[]; max: number } {
  const n = points.length || 1;
  const max = Math.max(1, ...points.map((p) => p.revenue), ...points.map((p) => p.adSpend ?? 0));
  const slot = w / n;
  const bw = Math.max(2, Math.round(slot * 0.6));
  const bars = points.map((p, i) => {
    const height = Math.round((p.revenue / max) * (h - 6) * 10) / 10;
    return { x: Math.round((i * slot + (slot - bw) / 2) * 10) / 10, y: Math.round((h - height) * 10) / 10, width: bw, height };
  });
  const line: string[] = [];
  let cur: string[] = [];
  points.forEach((p, i) => {
    if (p.adSpend == null) { if (cur.length) line.push(cur.join(' ')); cur = []; return; }
    cur.push(`${Math.round((i * slot + slot / 2) * 10) / 10},${Math.round((h - (p.adSpend / max) * (h - 6)) * 10) / 10}`);
  });
  if (cur.length) line.push(cur.join(' '));
  return { bars, line, max };
}
